import classNames from "classnames";
import React, { forwardRef, memo, useMemo } from "react";
import { Icon } from "../../graphic/Icon";
import styles from "./Item.scss";
import { SortableItem, SortableKnob } from "react-easy-sort";

export const Item = (props) => {
    const { index, sortable } = props;
    return sortable ? (
        <SortableItem key={index}>
            <ItemWrapper {...props} />
        </SortableItem>
    ) : (
        <ItemWrapper {...props} />
    );
};

// States can be given as a value, or as a function of the item's value
const resolveState = (state, value) => (typeof state === "function" ? state(value) : state);

/**
 * @param highlighted – blinks to draw attention, e.g. to an item that was just added
 * @param active – the item that is open or selected
 * @param disabled – faded and not clickable
 */
const ItemWrapper = forwardRef(function ItemWrapper(props, ref) {
    const { value, prependAlign, appendAlign, onClick, highlighted, active, disabled, render } = props;
    const isHighlighted = useMemo(() => resolveState(highlighted, value), [highlighted, value]);
    const isActive = useMemo(() => resolveState(active, value), [active, value]);
    const isDisabled = useMemo(() => resolveState(disabled, value), [disabled, value]);

    const element = (
        <div
            ref={ref}
            className={classNames(styles.item, {
                [styles.clickable]: onClick && !isDisabled,
                [styles.prependAlignTop]: prependAlign === "top",
                [styles.appendAlignTop]: appendAlign === "top",
                [styles.isHighlighted]: isHighlighted,
                [styles.isActive]: isActive,
                [styles.isDisabled]: isDisabled
            })}
            onClick={() => onClick && !isDisabled && onClick(value)}
            aria-disabled={isDisabled || undefined}
        >
            <ItemContent {...props} />
            <div className={styles.frame} />
        </div>
    );

    return render ? render(element) : element;
});

const ItemContent = memo(function ItemContent({ prepend, append, sortable, children }) {
    return (
        <div className={styles.inner}>
            {/* Prepend */}
            {prepend ? (
                <div className={styles.prepend}>
                    <div className={styles.wrapper}>
                        {Array.isArray(prepend)
                            ? prepend.map((elm, index) =>
                                  React.cloneElement(elm, {
                                      key: index
                                  })
                              )
                            : prepend}
                    </div>
                </div>
            ) : null}

            {/* Content */}
            <div className={styles.content}>
                <div className={styles.wrapper}>{children}</div>
            </div>

            {/* Append */}
            {append ? (
                <div className={styles.append}>
                    <div className={styles.wrapper}>
                        {Array.isArray(append)
                            ? append.map((elm, index) =>
                                  React.cloneElement(elm, {
                                      key: index
                                  })
                              )
                            : append}
                    </div>
                </div>
            ) : null}

            {/* Drag handle */}
            {sortable ? (
                <SortableKnob>
                    <div className={styles.handle} onClick={(e) => e.stopPropagation()}>
                        <Icon className={styles.icon} name="dot-matrix" />
                    </div>
                </SortableKnob>
            ) : null}
        </div>
    );
});
